import { db, recoverFromIndexedDBQuota } from "./indexeddb"
import type {
	AIMaskArtifactBundle,
	AIMaskArtifactBundleRecord,
	AIMaskArtifactChunkRecord,
} from "./ai-mask-artifact-types"
import { isBrowserStorageQuotaError, isStorageQuotaError } from "@/utils/error"
import { Sentry } from "@/telemetry/sentry"

export const AI_MASK_ARTIFACT_MAX_BYTES = 100 * 1024 * 1024
// Evict below the hard limit so every new write does not immediately trigger another eviction.
export const AI_MASK_ARTIFACT_LOW_WATER_BYTES = 90 * 1024 * 1024

const metadataByteEstimate = (metadata: Record<string, unknown>) =>
	new TextEncoder().encode(JSON.stringify(metadata)).byteLength

let writesDisabled = false
let reportedStorageFailure = false
let touchTimer: ReturnType<typeof setTimeout> | undefined
const pendingTouches = new Set<string>()

function reportStorageFailure(error: unknown) {
	if (reportedStorageFailure) {
		return
	}
	reportedStorageFailure = true
	Sentry.captureException(error)
}

function isValidChunk(chunk: AIMaskArtifactChunkRecord) {
	return (
		chunk.width > 0 &&
		chunk.height > 0 &&
		chunk.byteSize === chunk.data.byteLength &&
		chunk.data.byteLength === chunk.width * chunk.height &&
		(chunk.encoding === "u8-alpha-v1" || chunk.encoding === "u8-class-map-v1")
	)
}

function scheduleTouch(id: string) {
	// Cache reads can be frequent while rendering. Batch LRU timestamp writes to avoid making
	// every read a write transaction.
	pendingTouches.add(id)
	if (touchTimer) {
		return
	}
	touchTimer = setTimeout(() => {
		touchTimer = undefined
		const ids = [...pendingTouches]
		pendingTouches.clear()
		const now = Date.now()
		void db.aiMaskArtifactBundles
			.where("id")
			.anyOf(ids)
			.modify({ lastAccessedAt: now })
			.catch(reportStorageFailure)
	}, 1000)
}

async function deleteBundles(records: AIMaskArtifactBundleRecord[]) {
	if (records.length === 0) {
		return
	}
	const ids = records.map(({ id }) => id)
	// The manifest and its pixel chunks are one cache unit and must be removed together.
	await db.transaction("rw", db.aiMaskArtifactBundles, db.aiMaskArtifactChunks, async () => {
		await db.aiMaskArtifactChunks.where("bundleId").anyOf(ids).delete()
		await db.aiMaskArtifactBundles.bulkDelete(ids)
	})
}

export async function getAIMaskArtifactCacheSize() {
	const bundles = await db.aiMaskArtifactBundles.toArray()
	return bundles.reduce((total, bundle) => total + bundle.byteSize, 0)
}

export async function evictAIMaskArtifactsTo(targetBytes: number) {
	const bundles = await db.aiMaskArtifactBundles.orderBy("lastAccessedAt").toArray()
	let currentBytes = bundles.reduce((total, bundle) => total + bundle.byteSize, 0)
	const evicted: AIMaskArtifactBundleRecord[] = []
	for (const bundle of bundles) {
		if (currentBytes <= targetBytes) {
			break
		}
		evicted.push(bundle)
		currentBytes -= bundle.byteSize
	}
	await deleteBundles(evicted)
}

export async function evictAIMaskArtifactsIfNeeded() {
	const size = await getAIMaskArtifactCacheSize()
	if (size > AI_MASK_ARTIFACT_MAX_BYTES) {
		await evictAIMaskArtifactsTo(AI_MASK_ARTIFACT_LOW_WATER_BYTES)
	}
}

export async function readAIMaskArtifactBundle(
	id: string,
	roles?: string[],
): Promise<AIMaskArtifactBundle | null> {
	try {
		const bundle = await db.aiMaskArtifactBundles.get(id)
		if (!bundle) {
			return null
		}

		const chunks = roles
			? // Roles are independently stored so a People consumer can request only person:N.
				await db.aiMaskArtifactChunks
					.where("bundleId")
					.equals(id)
					.filter((chunk) => roles.includes(chunk.role))
					.toArray()
			: await db.aiMaskArtifactChunks.where("bundleId").equals(id).toArray()
		const storedRoles = bundle.chunkRoles
		const expectedRoles =
			roles ??
			(Array.isArray(storedRoles)
				? storedRoles.filter((role): role is string => typeof role === "string")
				: [])
		const valid =
			expectedRoles.length > 0 &&
			chunks.length === expectedRoles.length &&
			new Set(expectedRoles).size === expectedRoles.length &&
			expectedRoles.every((role) => chunks.some((chunk) => chunk.role === role)) &&
			chunks.every(isValidChunk)
		if (!valid) {
			// Cache data is disposable. Remove incomplete/corrupt bundles and let inference rebuild them.
			await deleteAIMaskArtifactBundle(id)
			return null
		}

		scheduleTouch(id)
		return { bundle, chunks }
	} catch (error) {
		reportStorageFailure(error)
		return null
	}
}

export async function readAIMaskArtifactBundleMetadata(
	id: string,
): Promise<AIMaskArtifactBundleRecord | null> {
	try {
		const bundle = await db.aiMaskArtifactBundles.get(id)
		if (!bundle) {
			return null
		}
		if (!Array.isArray(bundle.chunkRoles) || bundle.chunkRoles.length === 0) {
			await deleteAIMaskArtifactBundle(id)
			return null
		}
		scheduleTouch(id)
		return bundle
	} catch (error) {
		reportStorageFailure(error)
		return null
	}
}

async function putBundle(bundle: AIMaskArtifactBundleRecord, chunks: AIMaskArtifactChunkRecord[]) {
	// Publishing both stores in one transaction prevents readers from observing partial inference output.
	await db.transaction("rw", db.aiMaskArtifactBundles, db.aiMaskArtifactChunks, async () => {
		await db.aiMaskArtifactChunks.where("bundleId").equals(bundle.id).delete()
		await db.aiMaskArtifactChunks.bulkPut(chunks)
		await db.aiMaskArtifactBundles.put(bundle)
	})
}

export async function writeAIMaskArtifactBundle(
	bundleInput: Omit<
		AIMaskArtifactBundleRecord,
		"byteSize" | "createdAt" | "lastAccessedAt" | "chunkRoles"
	>,
	chunks: AIMaskArtifactChunkRecord[],
) {
	if (writesDisabled || chunks.some((chunk) => !isValidChunk(chunk))) {
		return false
	}
	const now = Date.now()
	const byteSize =
		chunks.reduce((total, chunk) => total + chunk.byteSize, 0) +
		metadataByteEstimate(bundleInput.metadata)
	if (byteSize > AI_MASK_ARTIFACT_MAX_BYTES) {
		return false
	}
	const bundle: AIMaskArtifactBundleRecord = {
		...bundleInput,
		chunkRoles: chunks.map(({ role }) => role),
		byteSize,
		createdAt: now,
		lastAccessedAt: now,
	}
	try {
		await putBundle(bundle, chunks)
		await evictAIMaskArtifactsIfNeeded()
		return true
	} catch (error) {
		let failure = error
		if (isBrowserStorageQuotaError(error) || isStorageQuotaError(error) === true) {
			try {
				await recoverFromIndexedDBQuota(error)
				await putBundle(bundle, chunks)
				await evictAIMaskArtifactsIfNeeded()
				return true
			} catch (retryError) {
				writesDisabled = true
				failure = retryError
			}
		}
		reportStorageFailure(failure)
		return false
	}
}

export async function deleteAIMaskArtifactBundle(id: string) {
	const bundle = await db.aiMaskArtifactBundles.get(id)
	if (bundle) {
		await deleteBundles([bundle])
	}
}

export async function deleteAIMaskArtifactsForPhoto(projectId: string, photoId: string) {
	try {
		const records = await db.aiMaskArtifactBundles
			.where("photoKey")
			.equals(`${projectId}:${photoId}`)
			.toArray()
		await deleteBundles(records)
	} catch (error) {
		reportStorageFailure(error)
	}
}

export async function deleteAIMaskArtifactsForProject(projectId: string) {
	try {
		const records = await db.aiMaskArtifactBundles
			.where("projectId")
			.equals(projectId)
			.toArray()
		await deleteBundles(records)
	} catch (error) {
		reportStorageFailure(error)
	}
}

export async function clearAIMaskArtifacts() {
	await db.transaction("rw", db.aiMaskArtifactBundles, db.aiMaskArtifactChunks, async () => {
		await db.aiMaskArtifactChunks.clear()
		await db.aiMaskArtifactBundles.clear()
	})
}

export function resetAIMaskArtifactCacheRuntimeState() {
	writesDisabled = false
	reportedStorageFailure = false
	pendingTouches.clear()
	if (touchTimer) {
		clearTimeout(touchTimer)
		touchTimer = undefined
	}
}
